import enviro from 'enviro';
import * as ClassicHublets from 'hubspot-url-utils/hublets';
import * as Hublets from 'hubspot-url-utils/hublets-next';
import { getFullUrlPure } from 'hubspot-url-utils/pure';
import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { Metrics } from './metrics';
const SEND_ERROR_METRIC = 'postmessage-trusted-transport-send-error';

// magpie-lib's safe-origins.ts builds preview origins as
// `https://${portalId}.hubspotpreview${isQA ? 'qa' : ''}-${hublet}.com`,
// so a `.hubspotpreview` substring partitions an already-trusted
// origin list into HubSpot frame vs preview origins without us
// maintaining our own URL parser.
const PREVIEW_HOST_INFIX = '.hubspotpreview';
const defaultHostnameReader = () => window.location.hostname;
let hostnameReader = defaultHostnameReader;
const getCurrentHostname = () => hostnameReader();
export const __setHostnameReaderForTests = reader => {
  hostnameReader = reader !== null && reader !== void 0 ? reader : defaultHostnameReader;
};
const defaultContentOriginReader = contentWindow => contentWindow.location.origin;
let contentOriginReader = defaultContentOriginReader;
export const __setContentOriginReaderForTests = reader => {
  contentOriginReader = reader !== null && reader !== void 0 ? reader : defaultContentOriginReader;
};
export const readContentWindowOrigin = contentWindow => contentOriginReader(contentWindow);

// TODO: replace hardcoding the preview origin below with the preview-domain
// API instead: https://tools.hubteam.com/api/get/api.hubspotqa.com%2Fcos-domains%2Fv1%2Finternal%2Fpreview-domain?headers=Accept%3A%20*%2F*%0AContent-Type%3A%20*%2F*&authType=app&requestId=5cef25ed-ba04-48da-81b2-f0b3ad250f97
const getPreviewOrigin = (portalId, isQA) => hublet => `https://${portalId}.hubspotpreview${isQA ? 'qa' : ''}-${hublet}.com`;
const getPreviewV2Origin = (portalId, isQA) => hublet => `https://${portalId}.hubspotpreview${isQA ? 'qa2' : '2'}-${hublet}.com`;
const getAllOriginsLegacy = () => {
  const classicHublets = Object.values(ClassicHublets);
  const hublets = Object.values(Hublets);
  const portalId = PortalIdParser.get();
  const localQa = classicHublets.map(hublet => getFullUrlPure('local', hublet, 'qa'));
  const qa = classicHublets.map(hublet => getFullUrlPure('app', hublet, 'qa'));
  const localProd = hublets.map(hublet => getFullUrlPure('local', hublet, 'prod'));
  const prod = hublets.map(hublet => getFullUrlPure('app', hublet, 'prod'));
  const previewOrigins = portalId ? [...hublets.map(getPreviewOrigin(portalId, false)), ...hublets.map(getPreviewOrigin(portalId, true)), ...hublets.map(getPreviewV2Origin(portalId, false)), ...hublets.map(getPreviewV2Origin(portalId, true))] : [];
  return [...localQa, ...qa, ...localProd, ...prod, ...previewOrigins];
};
const getAllowedOrigins = () => {
  let hublet;
  let isQa;
  try {
    hublet = enviro.getHublet();
    isQa = enviro.isQa();
  } catch (_unused) {
    return getAllOriginsLegacy();
  }
  const envName = isQa ? 'qa' : 'prod';
  const portalId = PortalIdParser.get();
  const localOrigin = getFullUrlPure('local', hublet, envName);
  const localHostname = new URL(localOrigin).hostname;
  const currentHostname = getCurrentHostname();
  const worktreeLocalOrigin = currentHostname.endsWith(`.${localHostname}`) ? `https://${currentHostname}` : null;
  const frameOrigins = [getFullUrlPure('app', hublet, envName), localOrigin, ...(worktreeLocalOrigin ? [worktreeLocalOrigin] : [])];
  const previewOrigins = portalId ? [getPreviewOrigin(portalId, isQa)(hublet), getPreviewV2Origin(portalId, isQa)(hublet)] : [];
  return [...frameOrigins, ...previewOrigins];
};
let cachedAllowedOriginPartition = null;
export const __resetTrustedOriginsCacheForTests = () => {
  cachedAllowedOriginPartition = null;
};
const getAllowedOriginPartition = () => {
  if (!cachedAllowedOriginPartition) {
    const frame = new Set();
    const preview = new Set();
    for (const origin of getAllowedOrigins()) {
      if (origin.includes(PREVIEW_HOST_INFIX)) {
        preview.add(origin);
      } else {
        frame.add(origin);
      }
    }
    cachedAllowedOriginPartition = {
      frame,
      preview
    };
  }
  return cachedAllowedOriginPartition;
};
export const isCurrentWindowHubspotPreview = () => getAllowedOriginPartition().preview.has(window.location.origin);
export const isHubspotFrameOrigin = origin => getAllowedOriginPartition().frame.has(origin);
export const isPortalHubspotPreviewOrigin = origin => getAllowedOriginPartition().preview.has(origin);
export const getHubspotFrameOriginsForCurrentEnv = () => Array.from(getAllowedOriginPartition().frame);
export const getHubspotPreviewOriginsForCurrentEnv = () => Array.from(getAllowedOriginPartition().preview);
const tryParseOrigin = src => {
  if (!src) return null;
  try {
    const url = new URL(src, window.location.href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch (_unused2) {
    return null;
  }
};
export const isTrustedOrigin = origin => origin === window.location.origin || isHubspotFrameOrigin(origin) || isPortalHubspotPreviewOrigin(origin);
export const getTrustedTargetOrigins = () => [...getHubspotFrameOriginsForCurrentEnv(), ...getHubspotPreviewOriginsForCurrentEnv()];
export const getTrustedChildFrameTargets = () => {
  const results = [];
  const iframes = document.getElementsByTagName('iframe');
  for (let i = 0; i < iframes.length; i++) {
    const iframe = iframes[i];
    const contentWindow = iframe.contentWindow;
    if (!contentWindow) continue;
    const origin = tryParseOrigin(iframe.src);
    if (!origin) continue;
    if (!isTrustedOrigin(origin)) continue;
    // If the iframe was redirected to a different (still-trusted) origin —
    // e.g. local.hubspotqa.com → lima.local.hubspotqa.com in a bend worktree —
    // use the actual current origin so postMessage targets the right window.
    // Accessing contentWindow.location throws SecurityError for cross-origin
    // frames; in that case we keep the src-derived origin.
    let resolvedOrigin = origin;
    try {
      const actualOrigin = contentOriginReader(contentWindow);
      if (actualOrigin !== origin && isTrustedOrigin(actualOrigin)) {
        resolvedOrigin = actualOrigin;
      }
    } catch (_unused3) {
      // Cross-origin iframe — keep src-derived origin.
    }
    results.push({
      target: contentWindow,
      origin: resolvedOrigin
    });
  }
  return results;
};
export const isMessageFromChildFrame = source => {
  if (!source) {
    return false;
  }
  for (let i = 0; i < window.frames.length; i++) {
    if (window.frames[i] === source) {
      return true;
    }
  }
  return false;
};
const handleSendError = (error, onError) => {
  if (onError) {
    onError(error);
    return;
  }
  Metrics.counter(SEND_ERROR_METRIC).increment();
  Raven.captureException(error, {
    fingerprint: ['trusted postMessage send error']
  });
};
export const sendToFrame = ({
  target,
  targetOrigin,
  message,
  onSent,
  onError
}) => {
  try {
    target.postMessage(message, targetOrigin);
    onSent === null || onSent === void 0 || onSent({
      targetOrigin
    });
  } catch (error) {
    handleSendError(error, onError);
  }
};
export const sendToTrustedOrigins = ({
  target,
  message,
  onSent,
  onError
}) => {
  for (const targetOrigin of getTrustedTargetOrigins()) {
    // Failures for non-matching origins are expected in this fan-out broadcast;
    // suppress them unless the caller explicitly wants to handle errors.
    sendToFrame({
      target,
      targetOrigin,
      message,
      onSent,
      onError: onError !== null && onError !== void 0 ? onError : () => {
        Metrics.counter(SEND_ERROR_METRIC).increment();
      }
    });
  }
};
export const sendToSameWindow = ({
  message,
  onSent,
  onError
}) => {
  try {
    const targetOrigin = window.location.origin;
    window.postMessage(message, targetOrigin);
    onSent === null || onSent === void 0 || onSent({
      targetOrigin
    });
  } catch (error) {
    handleSendError(error, onError);
  }
};